import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ThumbsUp, Trash2, Star, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import FeedbackStatusBadge from "./FeedbackStatusBadge";
import FeedbackComments from "./FeedbackComments";
import FeedbackScoringPanel from "./FeedbackScoringPanel";
import { type FeedbackStatus } from "@/types";
import { Separator } from "@/components/ui/separator";

type FeedbackDetailDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  id: string;
  title: string;
  description: string;
  status: FeedbackStatus;
  votes: number;
  hasVoted: boolean;
  created_at?: string;
  boardName?: string;
  onVote: (id: string) => void;
  isAdmin?: boolean;
  companyStatuses?: any[];
  onStatusChange?: (id: string, status: FeedbackStatus) => void;
  onDelete?: (id: string) => void;
  scoreMethod?: 'value_effort' | 'rice';
  value_score?: number;
  effort_score?: number;
  reach_score?: number;
  impact_score?: number;
  confidence_score?: number;
  effort_rice_score?: number;
  onSaveScores?: (
    id: string,
    data: {
      score_method: 'value_effort' | 'rice',
      value_score?: number,
      effort_score?: number,
      reach_score?: number,
      impact_score?: number,
      confidence_score?: number,
      effort_rice_score?: number,
    }
  ) => void;
};

const FeedbackDetailDialog = ({
  open,
  onOpenChange,
  id,
  title,
  description,
  status,
  votes,
  hasVoted,
  created_at,
  boardName,
  onVote,
  isAdmin = false,
  companyStatuses = [],
  onStatusChange,
  onDelete,
  scoreMethod = 'value_effort',
  value_score = 5,
  effort_score = 5,
  reach_score = 5,
  impact_score = 5,
  confidence_score = 5,
  effort_rice_score = 5,
  onSaveScores,
}: FeedbackDetailDialogProps) => {
  const [showScoringPanel, setShowScoringPanel] = useState(false);

  const currentStatus = companyStatuses.find(s => s.id === status || s.name.toLowerCase().replace(/\s+/g, '_') === status);
  const defaultStatus = companyStatuses.length > 0 ? companyStatuses[0] : null;
  const currentStatusId = currentStatus?.id || defaultStatus?.id || status;

  const formattedDate = created_at 
    ? format(new Date(created_at), 'MMM d, yyyy')
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-start justify-between gap-4">
            <DialogTitle className="text-xl font-semibold pr-8">{title}</DialogTitle>
          </div>
        </DialogHeader>

        <div className="flex flex-col md:flex-row overflow-hidden max-h-[calc(90vh-73px)]">
          {/* Main content */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
            <div>
              <p className="text-muted-foreground whitespace-pre-wrap">{description}</p>
            </div>

            <Separator />

            <FeedbackComments feedbackId={id} />
          </div>

          {/* Sidebar */}
          <div className="w-full md:w-80 border-l bg-muted/20 overflow-y-auto px-4 py-4 space-y-4 max-h-full">
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                  Votes
                </label>
                <Button 
                  variant={hasVoted ? "default" : "outline"}
                  size="sm"
                  className="w-full justify-start"
                  onClick={(e) => {
                    e.stopPropagation();
                    onVote(id);
                  }}
                >
                  <ThumbsUp className="w-4 h-4 mr-2" />
                  {votes} {votes === 1 ? 'vote' : 'votes'}
                </Button>
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                  Status
                </label>
                {isAdmin ? (
                  <Select
                    value={currentStatusId}
                    onValueChange={(value) => {
                      onStatusChange?.(id, value);
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {companyStatuses.map((statusItem) => (
                        <SelectItem key={statusItem.id} value={statusItem.id}>
                          {statusItem.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <FeedbackStatusBadge status={status} companyStatuses={companyStatuses} />
                )}
              </div>

              {boardName && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                    Board
                  </label>
                  <Badge variant="outline" className="w-full justify-start">
                    {boardName}
                  </Badge>
                </div>
              )}

              {formattedDate && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                    Created
                  </label>
                  <p className="text-sm">{formattedDate}</p>
                </div>
              )}

              {isAdmin && onSaveScores && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                    Prioritization
                  </label>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => setShowScoringPanel(!showScoringPanel)}
                  >
                    <Star className="w-4 h-4 mr-2" />
                    {showScoringPanel ? "Hide Scoring" : "Score Item"}
                  </Button>
                </div>
              )}
            </div>

            {isAdmin && showScoringPanel && onSaveScores && (
              <div className="pt-4 border-t">
                <FeedbackScoringPanel
                  id={id}
                  initialScoreMethod={scoreMethod}
                  initialValueScore={value_score}
                  initialEffortScore={effort_score}
                  initialReachScore={reach_score}
                  initialImpactScore={impact_score}
                  initialConfidenceScore={confidence_score}
                  initialEffortRiceScore={effort_rice_score}
                  onSaveScores={onSaveScores}
                />
              </div>
            )}

            {isAdmin && (
              <div className="pt-4 border-t">
                <Button
                  variant="destructive"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    onDelete?.(id);
                    onOpenChange(false);
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackDetailDialog;
